/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

const KEY = "element-shared-appearance";
const THEMES = new Set([
  "light",
  "dark",
  "light-high-contrast",
  "dark-high-contrast",
]);

/** Only the configured call/join pair shares this public preference. */
export function sharedThemeDomain(
  admin = import.meta.env.VITE_ADMIN_URL,
  guest = import.meta.env.VITE_GUEST_URL,
  current = window.location.origin,
): string | null {
  try {
    if (!admin || !guest) return null;
    const a = new URL(admin),
      g = new URL(guest);
    if (
      ![a.origin, g.origin].includes(current) ||
      ![a.protocol, g.protocol].every((p) => p === "https:" || p === "http:")
    )
      return null;
    // Host-only cookies also cover separate localhost ports in local QA.
    if (a.hostname === g.hostname) return "";
    const left = a.hostname.split(".").reverse(),
      right = g.hostname.split(".").reverse();
    const common: string[] = [];
    for (
      let i = 0;
      i < Math.min(left.length, right.length) && left[i] === right[i];
      i++
    )
      common.unshift(left[i]);
    return common.length >= 2 ? common.join(".") : null;
  } catch {
    return null;
  }
}

export function readSharedTheme(): string | undefined {
  if (sharedThemeDomain() === null) return undefined;
  try {
    const value = document.cookie
      .split(";")
      .map((v) => v.trim())
      .find((v) => v.startsWith(KEY + "="))
      ?.slice(KEY.length + 1);
    return value && THEMES.has(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

export function saveSharedTheme(theme: string): void {
  const domain = sharedThemeDomain();
  if (domain === null || !THEMES.has(theme) || readSharedTheme() === theme)
    return;
  try {
    document.cookie = `${KEY}=${theme}; Path=/; Max-Age=31536000; SameSite=Lax${domain ? `; Domain=${domain}` : ""}${location.protocol === "https:" ? "; Secure" : ""}`;
  } catch {
    /* A blocked preference cookie must not affect the call. */
  }
}
